/**
 * Custom Cart Notification - Bottom Slide-up Popup
 * Integrates with product-form.js via the same API as Giant's CartNotification.
 * Updates custom navbar cart badges using section-rendered data (zero extra HTTP requests).
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.overlay = document.getElementById('cart-notification-overlay');
    this.autoCloseTimer = null;

    if (!this.notification) return;

    // Keyboard close
    this.notification.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    // Close button(s)
    this.querySelectorAll('.cart-notification__close').forEach((btn) =>
      btn.addEventListener('click', this.close.bind(this))
    );

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }

    // Pause auto-close on hover
    this.notification.addEventListener('mouseenter', () => {
      clearTimeout(this.autoCloseTimer);
    });

    this.notification.addEventListener('mouseleave', () => {
      if (this.notification.classList.contains('active')) {
        this.autoCloseTimer = setTimeout(() => this.close(), 3000);
      }
    });
  }

  open() {
    if (!this.notification) return;

    // Clear previous timers and focus traps
    clearTimeout(this.autoCloseTimer);
    removeTrapFocus(this.activeElement);

    // Always reset to closed state first — this is critical for re-opens
    this.notification.classList.remove('active');
    if (this.overlay) this.overlay.classList.remove('active');

    // Force browser reflow so the removal is registered before re-adding
    // Without this, adding 'active' back is a no-op and the transition never fires
    void this.notification.offsetHeight;

    // Now open with a fresh transition
    this.notification.classList.add('active');
    if (this.overlay) this.overlay.classList.add('active');

    // Auto-close after 5 seconds
    this.autoCloseTimer = setTimeout(() => this.close(), 5000);

    // Focus trap after slide-in transition completes
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
  }

  close() {
    clearTimeout(this.autoCloseTimer);
    if (this.notification) this.notification.classList.remove('active');
    if (this.overlay) this.overlay.classList.remove('active');

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      if (!el || !parsedState.sections || !parsedState.sections[section.id]) return;
      try {
        el.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      } catch (e) {
        // console.error('Cart notification: Failed to render section', section.id, e);
      }
    });

    // Extract cart count from the rendered button section (no HTTP request needed)
    this.updateBadgesFromSection();

    this.open();
  }

  updateBadgesFromSection() {
    const buttonEl = document.getElementById('cart-notification-button');
    if (!buttonEl) return;
    const countSpan = buttonEl.querySelector('[data-cart-count]');
    if (countSpan && typeof window.updateCartBadges === 'function') {
      const count = parseInt(countSpan.dataset.cartCount, 10);
      if (!isNaN(count)) {
        window.updateCartBadges(count);
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
